import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import Select, { components } from "react-select";
import { toast } from "react-toastify";
import { Button, ErrorModal, FormWrapper, SubmitButton } from "../../components";
import { daysToHoldPlotOptions, emiOptions } from "../../constants";

const Option = (props) => {
  return (
    <div>
      <components.Option {...props}>
        <div className="row">
          <label htmlFor={`plot_${props.data.value}`}>{props.label}</label>
          <input type="checkbox" name={`plot_${props.data.value}`} checked={props.isSelected} onChange={() => null} />
        </div>
      </components.Option>
    </div>
  );
};

function NewPhaseForm({
  handleSubmit,
  handleCancel,
  showCancel = true,
  disableInputs = false,
  data,
  isEdit,
  formId,
  projectData,
}) {
  const [inputData, setInputData] = useState({
    agent_commission: "",
    phase_name: "",
    number_of_plots: "",
    company_commission: "",
    start_range: "",
    end_range: "",
    plot_rate: "",
    max_negotiate_rate: "",
    emi: "",
    monthly_emi_limit: 1,
    down_payment: 1,
    rate_limit: "",
    token_percent: "",
    token_percent_limit: "",
    registration_charges: "",
    development_charges: "",
    available_plots: [],
    unavailable_plots: [],
    on_hold_plots: [],
    days_to_put_on_hold: "",
    rate_per_day_to_hold_plot: "",
  });
  const [editedData, setEditedData] = useState({});
  const [projectDetails, setProjectDetails] = useState({});

  useEffect(() => {
    if (data) {
      let dataToInput = inputData;

      if (projectData) {
        Object.keys(dataToInput).forEach((key) => {
          if (dataToInput[key]) {
            dataToInput[key] = projectData[key] ?? dataToInput[key];
          }
        });
      }

      dataToInput = {
        ...data,
        agent_commission: data.agent_commission?.replace(".00", ""),
        company_commission: data.company_commission?.replace(".00", ""),
        start_range: data.start_range?.replace(".00", ""),
        end_range: data.end_range?.replace(".00", ""),
        plot_rate: data.plot_rate?.replace(".00", ""),
        max_negotiate_rate: data.max_negotiate_rate?.replace(".00", ""),
        emi: +data.emi?.replace(".00", ""),
        rate_limit: data.rate_limit?.replace(".00", ""),
        token_percent: data.token_percent?.replace(".00", ""),
        token_percent_limit: data.token_percent_limit?.replace(".00", ""),
        registration_charges: data.registration_charges?.replace(".00", ""),
        development_charges: data.development_charges?.replace(".00", ""),
        rate_per_day_to_hold_plot: data.rate_per_day_to_hold_plot?.replace(".00", ""),
        phase_name: data.phase_name,
      };
      setInputData(dataToInput);
      setAvailablePlots(
        data.available_plots
          .filter((plot) => plot && plot <= data.number_of_plots)
          .map((plot) => ({ value: plot, label: `Plot ${plot}` }))
      );
      setUnavailablePlots(
        data.unavailable_plots
          .filter((plot) => plot && plot <= data.number_of_plots)
          .map((plot) => ({ value: plot, label: `Plot ${plot}` }))
      );
      setOnHoldPlots(
        data.on_hold_plots
          .filter((plot) => plot && plot <= data.number_of_plots)
          .map((plot) => ({ value: plot, label: `Plot ${plot}` }))
      );
    }
  }, [data]);

  const [phaseEmiOptions, setPhaseEmiOptions] = useState([]);

  useEffect(() => {
    if (projectData) {
      if (!data) {
        let projectInputData = { ...inputData };
        Object.keys(inputData).forEach((key) => {
          if (projectData[key]) {
            projectInputData[key] = projectData[key];
          }
        });
        setInputData(projectInputData);
      }
      setProjectDetails(projectData);
      setPhaseEmiOptions(
        emiOptions?.filter((option) => {
          return option.value <= projectData.emi;
        })
      );
    }
  }, [projectData]);

  const [plotsNotTaggedMessage, setPlotsNotTaggedMessage] = useState("");
  const [showPlotsNotTaggedModal, setShowPlotsNotTaggedModal] = useState(false);

  const [availablePlots, setAvailablePlots] = useState([]);
  const [unavailablePlots, setUnavailablePlots] = useState([]);
  const [onHoldPlots, setOnHoldPlots] = useState([]);

  const plotOptions = useMemo(
    () =>
      inputData.number_of_plots && inputData.number_of_plots > 0
        ? Array.from(
            { length: inputData.number_of_plots },
            (_, index) =>
              index < inputData.number_of_plots && {
                value: index + 1,
                label: `Plot ${index + 1}`,
              }
          )
        : [],
    [inputData.number_of_plots]
  );

  useEffect(() => {
    getPlotOptions();
  }, [plotOptions]);

  const [unavailablePlotsOptions, setUnavailablePlotsOptions] = useState([]);
  const [availablePlotsOptions, setAvailablePlotsOptions] = useState([]);
  const [onHoldPlotsOptions, setOnHoldPlotsOptions] = useState([]);

  const getPlotOptions = () => {
    if (isEdit || data) {
      const availableValues = availablePlots.map((plot) => plot.value);
      const unavailableValues = unavailablePlots.map((plot) => plot.value);
      const onHoldValues = onHoldPlots.map((plot) => plot.value);
      const availableOptions = plotOptions.filter(
        (plot) =>
          !inputData.ongoing_plots.includes(plot.value) &&
          !inputData.completed_plots.includes(plot.value) &&
          !inputData.canceled_plots.includes(plot.value) &&
          !unavailableValues.includes(plot.value) &&
          !onHoldValues.includes(plot.value)
      );
      const unavailableOptions = plotOptions.filter(
        (plot) =>
          !inputData.ongoing_plots.includes(plot.value) &&
          !inputData.completed_plots.includes(plot.value) &&
          !inputData.canceled_plots.includes(plot.value) &&
          !availableValues.includes(plot.value) &&
          !onHoldValues.includes(plot.value)
      );
      const onHoldOptions = plotOptions.filter(
        (plot) =>
          !inputData.ongoing_plots.includes(plot.value) &&
          !inputData.completed_plots.includes(plot.value) &&
          !inputData.canceled_plots.includes(plot.value) &&
          !availableValues.includes(plot.value) &&
          !unavailableValues.includes(plot.value)
      );
      onHoldOptions.length > onHoldPlots.length && onHoldOptions.unshift({ value: "*", label: "All Plots" });
      availableOptions.length > availablePlots.length && availableOptions.unshift({ value: "*", label: "All Plots" });
      unavailableOptions.length > unavailablePlots.length &&
        unavailableOptions.unshift({
          value: "*",
          label: "All Plots",
        });
      setAvailablePlotsOptions(availableOptions);
      setUnavailablePlotsOptions(unavailableOptions);
      setOnHoldPlotsOptions(onHoldOptions);
    } else {
      const availableOptions = plotOptions.filter((plot) => !unavailablePlots.includes(plot));
      const unavailableOptions = plotOptions.filter((plot) => !availablePlots.includes(plot));

      availableOptions.length > availablePlots.length && availableOptions.unshift({ value: "*", label: "All Plots" });
      unavailableOptions.length > unavailablePlots.length &&
        unavailableOptions.unshift({
          value: "*",
          label: "All Plots",
        });
      setAvailablePlotsOptions(availableOptions);
      setUnavailablePlotsOptions(unavailableOptions);
    }
  };

  const handleAvailablePlotsSelect = (selectedOptions) => {
    const selectedValues = selectedOptions.map((option) => option.value);
    if (selectedValues.includes("*")) {
      setAvailablePlots(availablePlotsOptions.filter((option) => option.value !== "*"));
      setInputData((prev) => ({ ...prev, available_plots: selectedValues.filter((value) => value !== "*") }));
      setEditedData((prev) => ({ ...prev, available_plots: selectedValues.filter((value) => value !== "*") }));

      return;
    }

    setAvailablePlots(selectedOptions);
    setInputData((prev) => ({ ...prev, available_plots: selectedValues }));
    setEditedData((prev) => ({ ...prev, available_plots: selectedValues }));
  };

  const handleOnHoldPlotsSelect = (selectedOptions) => {
    const selectedValues = selectedOptions.map((option) => option.value);
    if (selectedValues.includes("*")) {
      setOnHoldPlots(onHoldPlotsOptions.filter((option) => option.value !== "*"));
      setInputData((prev) => ({ ...prev, on_hold_plots: selectedValues.filter((value) => value !== "*") }));
      const editedValues = selectedValues.filter((value) => value !== "*");
      setEditedData((prev) => ({ ...prev, onhold_plots: editedValues }));
      return;
    }
    setOnHoldPlots(selectedOptions);
    setInputData((prev) => ({ ...prev, on_hold_plots: selectedValues }));
    setEditedData((prev) => ({ ...prev, onhold_plots: selectedValues }));
  };

  useEffect(() => {
    getPlotOptions();
  }, [availablePlots, unavailablePlots, onHoldPlots]);

  const handleUnavailablePlotsSelect = (selectedOptions) => {
    const selectedValues = selectedOptions.map((option) => option.value);
    if (selectedValues.includes("*")) {
      setUnavailablePlots(unavailablePlotsOptions.filter((option) => option.value !== "*"));
      setInputData((prev) => ({ ...prev, unavailable_plots: selectedValues.filter((value) => value !== "*") }));
      setEditedData((prev) => ({ ...prev, unavailable_plots: selectedValues.filter((value) => value !== "*") }));
      return;
    }
    setUnavailablePlots(selectedOptions);
    setInputData((prev) => ({ ...prev, unavailable_plots: selectedValues }));
    setEditedData((prev) => ({ ...prev, unavailable_plots: selectedValues }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;
    if (name === "number_of_plots" && value > 1000) {
      toast.dismiss();
      toast.error("Number of plots cannot be more than 1000");
      newValue = 1000;
    }

    setInputData((prev) => ({ ...prev, [name]: newValue }));
    setEditedData((prev) => ({ ...prev, [name]: newValue }));
  };

  const handleSelectChange = (newValue, name) => {
    setInputData((prev) => ({ ...prev, [name]: newValue.value }));
    setEditedData((prev) => ({ ...prev, [name]: newValue.value }));
  };

  const validateFormData = (formData) => {
    let notTaggedPlots = [];
    const updatedData = formData;
    if (isEdit) {
      const savedPlots = [...inputData.ongoing_plots, ...inputData.completed_plots, ...inputData.canceled_plots];
      const availablePlotValues = availablePlots.map((plot) => plot.value);
      const unavailablePlotValues = unavailablePlots.map((plot) => plot.value);
      const onHoldPlotValues = onHoldPlots.map((plot) => plot.value);
      Array.from({ length: formData.number_of_plots }, (_, index) => index + 1).forEach((plot) => {
        if (
          !savedPlots.includes(plot) &&
          !availablePlotValues.includes(plot) &&
          !unavailablePlotValues.includes(plot) &&
          !onHoldPlotValues.includes(plot)
        ) {
          notTaggedPlots.push(plot);
        }
      });
    } else {
      Array.from({ length: formData.number_of_plots }, (_, index) => index + 1).forEach((plot) => {
        if (!formData.available_plots.includes(plot) && !formData.unavailable_plots.includes(plot)) {
          notTaggedPlots.push(plot);
        }
      });
    }
    if (notTaggedPlots.length > 0) {
      setPlotsNotTaggedMessage(
        `${notTaggedPlots.length > 1 ? "Plots" : "Plot"} ${notTaggedPlots.join(", ")} ${
          notTaggedPlots.length > 1 ? "are" : "is"
        } not tagged into any of the above categories.`
      );
      setShowPlotsNotTaggedModal(true);
      return { status: false, data: null };
    }

    toast.info(
      Object.keys(formData).map((key) => `Please enter a valid ${key}`),
      { autoClose: false }
    );

    if (Object.values(formData).some((value) => !value || value === "")) {
      toast.error("Please enter valid details");
      return { status: false, data: null };
    }

    return { status: true, data: updatedData };
  };
  const onCancel = () => {
    handleCancel();
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();

    if (isEdit && Object.keys(editedData).length === 0) return onCancel();

    const dataToSubmit = isEdit ? editedData : inputData;
    const dataToSubmitKeys = Object.keys(dataToSubmit);
    if (dataToSubmitKeys.includes("unavailable_plots"))
      dataToSubmit.unavailable_plots = unavailablePlots.map(({ value }) => parseInt(value));
    if (dataToSubmitKeys.includes("available_plots"))
      dataToSubmit.available_plots = availablePlots.map(({ value }) => parseInt(value));
    if (dataToSubmitKeys.includes("on_hold_plots"))
      dataToSubmit.on_hold_plots = onHoldPlots.map(({ value }) => parseInt(value));
    if (dataToSubmitKeys.includes("onhold_plots"))
      dataToSubmit.onhold_plots = onHoldPlots.map(({ value }) => parseInt(value));
    if (dataToSubmitKeys.includes("start_range")) {
      dataToSubmit.start_range = parseFloat(dataToSubmit.start_range);
      dataToSubmit.plot_rate = parseFloat(dataToSubmit.start_range);
    }
    if (dataToSubmitKeys.includes("end_range")) {
      dataToSubmit.end_range = parseFloat(dataToSubmit.end_range);
    }
    if (dataToSubmitKeys.includes("plot_rate")) dataToSubmit.plot_rate = parseFloat(dataToSubmit.start_range);
    if (dataToSubmitKeys.includes("max_negotiate_rate"))
      dataToSubmit.max_negotiate_rate = parseFloat(dataToSubmit.max_negotiate_rate);

    if (dataToSubmitKeys.includes("rate_limit")) dataToSubmit.rate_limit = parseFloat(dataToSubmit.rate_limit);
    if (dataToSubmitKeys.includes("token_percent")) dataToSubmit.token_percent = parseFloat(dataToSubmit.token_percent);
    if (dataToSubmitKeys.includes("token_percent_limit"))
      dataToSubmit.token_percent_limit = parseFloat(dataToSubmit.token_percent_limit);
    if (dataToSubmitKeys.includes("registration_charges"))
      dataToSubmit.registration_charges = parseFloat(dataToSubmit.registration_charges);
    if (dataToSubmitKeys.includes("development_charges"))
      dataToSubmit.development_charges = parseFloat(dataToSubmit.development_charges);
    if (dataToSubmitKeys.includes("rate_per_day_to_hold_plot"))
      dataToSubmit.rate_per_day_to_hold_plot = parseFloat(dataToSubmit.rate_per_day_to_hold_plot);

    const { data, status } = validateFormData(dataToSubmit);
    if (!status) return;

    handleSubmit(data);
    onCancel();
  };

  return (
    <>
      <FormWrapper id={formId} onSubmit={handleFormSubmit}>
        <div className="column">
          <section className="section grid_3">
            <div className="column">
              <div className="row">
                <span className="label">Agent Commission</span>
                <div className="input_with_suffix_wrapper full">
                  <input
                    type="number"
                    name="agent_commission"
                    required
                    min={0}
                    onChange={handleInputChange}
                    value={inputData.agent_commission}
                    placeholder="Enter percent"
                    className="input"
                    max={100 - inputData.company_commission}
                    disabled={disableInputs}
                  />
                  <span className="suffix">%</span>
                </div>
              </div>
              <div className="row">
                <span className="label">Phase name</span>
                <input
                  type="text"
                  name="phase_name"
                  required
                  onChange={handleInputChange}
                  value={projectData.phase_name}
                  placeholder="Enter Phase Name"
                  className="full input"
                  disabled={disableInputs}
                  readOnly
                />
              </div>
              <div className="row">
                <span className="label">Number of plots</span>
                <input
                  type="number"
                  name="number_of_plots"
                  required
                  onChange={handleInputChange}
                  value={inputData.number_of_plots}
                  placeholder="Enter number of plots"
                  className="full input"
                  min={1}
                  max={1000}
                  disabled={disableInputs}
                />
              </div>
              <div className="row">
                <span className="label">Company's Commission</span>
                <div className="input_with_suffix_wrapper full">
                  <input
                    type="number"
                    name="company_commission"
                    required
                    onChange={handleInputChange}
                    value={inputData.company_commission}
                    placeholder="Enter Percentage"
                    className="full input"
                    min={0}
                    max={100 - inputData.agent_commission}
                    disabled={disableInputs}
                  />
                  <span className="suffix">%</span>
                </div>
              </div>
              <div className="row">
                <span className="label">Rate range (per sq.ft.)</span>
                <div className="row rate_range">
                  <div className="input_with_prefix_wrapper">
                    <span className="prefix">₹</span>
                    <input
                      type="number"
                      name="start_range"
                      required
                      onChange={handleInputChange}
                      value={inputData.start_range}
                      placeholder="Starting range"
                      className="input"
                      disabled={disableInputs}
                      max={inputData.end_range}
                    />
                  </div>
                  <span className="label">To</span>
                  <div className="input_with_prefix_wrapper">
                    <span className="prefix">₹</span>
                    <input
                      name="end_range"
                      type="number"
                      required
                      onChange={handleInputChange}
                      value={inputData.end_range}
                      placeholder="End range"
                      className="input"
                      disabled={disableInputs}
                      min={inputData.start_range}
                    />
                  </div>
                </div>
              </div>
              <div className="row">
                <span className="label">EMI Rate Limit (per sq.ft.)</span>
                <div className="input_with_prefix_wrapper full">
                  <span className="prefix">₹</span>
                  <input
                    type="number"
                    name="max_negotiate_rate"
                    required
                    onChange={handleInputChange}
                    value={inputData.max_negotiate_rate}
                    placeholder="Enter Amount (₹)"
                    className="full input"
                    disabled={disableInputs}
                    min={0}
                  />
                </div>
              </div>
              <div className="row">
                <span className="label">EMI</span>
                <Select
                  name="emi"
                  value={phaseEmiOptions.find((option) => option.value === inputData.emi)}
                  onChange={(newValue) => handleSelectChange(newValue, "emi")}
                  options={phaseEmiOptions}
                  placeholder="Select months"
                  className="select full"
                  isDisabled={disableInputs}
                  required
                  styles={{
                    control: (base) => ({
                      ...base,
                      border: "1px solid var(--gray-light)",
                      boxShadow: "none",
                      ":hover": {
                        border: "1px solid var(--gray-light)",
                      },
                    }),
                  }}
                />
              </div>
              <div className="row">
                <span className="label">Down Payment Rate Limit (per sq.ft.)</span>
                <div className="input_with_prefix_wrapper full">
                  <span className="prefix">₹</span>
                  <input
                    type="number"
                    name="rate_limit"
                    min={0}
                    max={inputData.end_range}
                    onChange={handleInputChange}
                    value={inputData.rate_limit}
                    placeholder="Enter amount (₹)"
                    className="input"
                    disabled={disableInputs}
                    required
                  />
                </div>
              </div>
              <div className="row">
                <span className="label">Token percent</span>
                <div className="input_with_suffix_wrapper full">
                  <input
                    type="number"
                    name="token_percent"
                    min={0}
                    max={100}
                    onChange={handleInputChange}
                    value={inputData.token_percent}
                    placeholder="Enter %"
                    className="input"
                    disabled={disableInputs}
                    required
                  />
                  <span className="suffix">%</span>
                </div>
              </div>
              <div className="row">
                <span className="label">Token percent limit</span>
                <div className="input_with_suffix_wrapper full">
                  <input
                    type="number"
                    name="token_percent_limit"
                    min={0}
                    max={inputData.token_percent}
                    onChange={handleInputChange}
                    value={inputData.token_percent_limit}
                    placeholder="Enter %"
                    className="input"
                    disabled={disableInputs}
                    required
                  />
                  <span className="suffix">%</span>
                </div>
              </div>
              <div className="row">
                <span className="label">
                  Registration Charges <br /> + Advocate Fees + Satbara
                </span>
                <div className="input_with_prefix_wrapper full">
                  <span className="prefix">₹</span>
                  <input
                    type="number"
                    name="registration_charges"
                    min={0}
                    onChange={handleInputChange}
                    value={inputData.registration_charges}
                    placeholder="Enter amount (₹)"
                    className="input"
                    disabled={disableInputs}
                    required
                  />
                </div>
              </div>
              <div className="row">
                <span className="label">
                  Development Charges <br />
                  (Water & Electricity)
                </span>
                <div className="input_with_prefix_wrapper full">
                  <span className="prefix">₹</span>
                  <input
                    type="number"
                    name="development_charges"
                    min={0}
                    onChange={handleInputChange}
                    value={inputData.development_charges}
                    placeholder="Enter amount (₹)"
                    className="input"
                    disabled={disableInputs}
                    required
                  />
                </div>
              </div>
              <div className="row">
                <span className="label">Put on hold</span>
                <Select
                  name="days_to_put_on_hold"
                  value={daysToHoldPlotOptions.find((option) => option.value === inputData.days_to_put_on_hold)}
                  onChange={(newValue) => handleSelectChange(newValue, "days_to_put_on_hold")}
                  options={daysToHoldPlotOptions}
                  placeholder="Select option"
                  className="select full"
                  isDisabled={disableInputs}
                  required
                  styles={{
                    control: (base) => ({
                      ...base,
                      border: "1px solid var(--gray-light)",
                      boxShadow: "none",
                      ":hover": {
                        border: "1px solid var(--gray-light)",
                      },
                    }),
                  }}
                />
              </div>
              <div className="row">
                <span className="label">Rate/day to hold plot</span>
                <div className="input_with_prefix_wrapper full">
                  <span className="prefix">₹</span>
                  <input
                    type="number"
                    name="rate_per_day_to_hold_plot"
                    min={0}
                    onChange={handleInputChange}
                    value={inputData.rate_per_day_to_hold_plot}
                    placeholder="Enter amount (₹)"
                    className="input"
                    disabled={disableInputs}
                    required
                  />
                </div>
              </div>
            </div>
            <div className="vertical_divider"></div>
            <div className="column">
              <div className="column">
                <div className="phase_select_wrapper">
                  <div className="label shrink">
                    <div className="color_swatch status_available"></div>
                    <span className="label">Available Plots</span>
                  </div>
                  <Select
                    name="available_plots"
                    value={availablePlots}
                    onChange={(newValue) => handleAvailablePlotsSelect(newValue)}
                    options={availablePlotsOptions}
                    onMenuOpen={getPlotOptions}
                    placeholder="Select Plot"
                    className="select full"
                    isDisabled={disableInputs}
                    isClearable
                    isMulti
                    closeMenuOnSelect={false}
                    components={{ Option }}
                    styles={{
                      control: (base) => ({
                        ...base,
                        border: "1px solid var(--gray-light)",
                        boxShadow: "none",
                        ":hover": {
                          border: "1px solid var(--gray-light)",
                        },
                      }),
                    }}
                  />
                </div>
                <div className="phase_select_wrapper">
                  <div className="label shrink">
                    <div className="color_swatch status_unavailable"></div>
                    <span className="label">Unavailable Plots</span>
                  </div>
                  <Select
                    name="unavailable_plots"
                    value={unavailablePlots}
                    onChange={(newValue) => handleUnavailablePlotsSelect(newValue)}
                    options={unavailablePlotsOptions}
                    onMenuOpen={getPlotOptions}
                    placeholder="Select Plot"
                    className="select full"
                    isDisabled={disableInputs}
                    isClearable
                    isMulti
                    closeMenuOnSelect={false}
                    components={{ Option }}
                    styles={{
                      control: (base) => ({
                        ...base,
                        border: "1px solid var(--gray-light)",
                        boxShadow: "none",
                        ":hover": {
                          border: "1px solid var(--gray-light)",
                        },
                      }),
                    }}
                  />
                </div>
                {isEdit && (
                  <div className="phase_select_wrapper">
                    <div className="label shrink">
                      <div className="color_swatch status_on-hold"></div>
                      <span className="label">On Hold Plots</span>
                    </div>
                    <Select
                      name="on_hold_plots"
                      value={onHoldPlots}
                      onChange={(newValue) => handleOnHoldPlotsSelect(newValue)}
                      options={onHoldPlotsOptions}
                      onMenuOpen={getPlotOptions}
                      placeholder="Select Plot"
                      className="select full"
                      isDisabled={disableInputs}
                      isClearable
                      isMulti
                      closeMenuOnSelect={false}
                      components={{ Option }}
                      styles={{
                        control: (base) => ({
                          ...base,
                          border: "1px solid var(--gray-light)",
                          boxShadow: "none",
                          ":hover": {
                            border: "1px solid var(--gray-light)",
                          },
                        }),
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          </section>
          <div className="row end space-top">
            {showCancel && (
              <Button className="ghost_outline_round" onClick={onCancel} end>
                Cancel
              </Button>
            )}
            <SubmitButton form={formId} className="cta" end disabled={disableInputs}>
              Save
            </SubmitButton>
          </div>
        </div>
      </FormWrapper>
      {showPlotsNotTaggedModal &&
        createPortal(
          <ErrorModal
            isVisible={showPlotsNotTaggedModal}
            handleClose={() => setShowPlotsNotTaggedModal(false)}
            message={plotsNotTaggedMessage}
            name="plots_not_tagged"
          />,
          document.body
        )}
    </>
  );
}

export default NewPhaseForm;
